import { resolveAgoraAppId } from "../config/agoraConfig";

const MIC_CONSTRAINTS = {
  AEC: true,
  ANS: true,
  AGC: true,
};

const AUDIO_GUM = {
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: true,
};

const DEVICE_SYNC_DEBOUNCE_MS = 300;

/**
 * Validate Agora session fields returned by the support call API.
 */
export function parseSupportAgoraSession(data) {
  const appId = resolveAgoraAppId(typeof data?.appId === "string" ? data.appId : null);
  const channelName = String(data?.channelName ?? data?.channel_name ?? "").trim();
  const token = String(data?.token ?? "").trim();
  const uidRaw = data?.uid;
  const uid = Number(uidRaw);
  const numericUid = Number.isFinite(uid) && uid >= 0 ? uid : 0;

  if (!channelName) {
    throw new Error("Missing Agora channel name from server");
  }
  if (!token || !token.startsWith("007")) {
    throw new Error(
      "Invalid Agora token from server. Restart the API after updating AGORA_APP_ID / AGORA_APP_CERTIFICATE."
    );
  }

  return { appId, channelName, token, uid: numericUid };
}

function stopMediaStream(stream) {
  try {
    stream?.getTracks?.().forEach((track) => {
      try {
        track.stop();
      } catch {
        /* ignore */
      }
    });
  } catch {
    /* ignore */
  }
}

/**
 * Ensure the browser has microphone permission before starting a call.
 * Triggers the browser prompt if the permission has not been decided yet.
 * @returns {Promise<{ granted: boolean, reason?: "denied" | "no-device" | "unsupported" | "error", error?: unknown }>}
 */
export async function ensureMicrophonePermission() {
  if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia) {
    return { granted: false, reason: "unsupported" };
  }

  try {
    if (navigator.permissions?.query) {
      const status = await navigator.permissions.query({ name: "microphone" });
      if (status.state === "denied") {
        return { granted: false, reason: "denied" };
      }
    }
  } catch {
    // Permissions API may not support "microphone" (Safari / some Linux browsers).
  }

  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: AUDIO_GUM });
    // Must release immediately — holding this stream steals the mic from Agora
    // on Windows WASAPI exclusive, PulseAudio, and some macOS/Safari setups.
    stopMediaStream(stream);
    return { granted: true };
  } catch (err) {
    const name = err?.name || "";
    if (name === "NotAllowedError" || name === "SecurityError" || name === "PermissionDeniedError") {
      return { granted: false, reason: "denied" };
    }
    if (name === "NotFoundError" || name === "DevicesNotFoundError") {
      return { granted: false, reason: "no-device" };
    }
    return { granted: false, reason: "error", error: err };
  }
}

let activeSupportClient = null;
let joinInFlight = null;

function isAgoraConflictError(err) {
  const code = String(err?.code || err?.message || "").toUpperCase();
  return (
    code.includes("UID_CONFLICT") ||
    code.includes("CHANNEL_CONFLICT") ||
    code.includes("ADAPTER_SEND_CONFLICT") ||
    code.includes("INVALID_OPERATION")
  );
}

function looksLikeHeadsetOrBluetooth(label) {
  return /bluetooth|airpods|headset|ear.?bud|ear.?phone|hands.?free|hfp|a2dp|beats|galaxy buds|pixel buds|wh-?\d|sony|jabra|bose|logitech/i.test(
    label || ""
  );
}

function looksLikeDefaultDevice(label, deviceId) {
  const id = String(deviceId || "").toLowerCase();
  return (
    id === "default" ||
    id === "communications" ||
    /default|communications/i.test(label || "")
  );
}

function looksLikeLoopback(label) {
  return /monitor of|loopback|stereo mix|what u hear|wave out mix|blackhole|vb-audio|soundflower/i.test(
    label || ""
  );
}

function usableDevices(list) {
  return (Array.isArray(list) ? list : []).filter(
    (d) => d?.deviceId && d.deviceId !== "communications" && !looksLikeLoopback(d.label)
  );
}

/**
 * Headset/BT when plugged in; otherwise OS default (null = let Agora follow the OS).
 * Never pin mics[0] — on Linux/Windows that is often HDMI, Stereo Mix, or a monitor.
 */
async function pickPreferredMicrophoneId(AgoraRTC) {
  try {
    const mics = usableDevices(await AgoraRTC.getMicrophones(true));
    if (!mics.length) return null;
    const headset = mics.find((d) => looksLikeHeadsetOrBluetooth(d.label));
    if (headset) return headset.deviceId;
    const sysDefault = mics.find((d) => looksLikeDefaultDevice(d.label, d.deviceId));
    return sysDefault?.deviceId || null;
  } catch {
    return null;
  }
}

async function pickPreferredPlaybackId(AgoraRTC) {
  try {
    const devices = usableDevices(await AgoraRTC.getPlaybackDevices(true));
    if (!devices.length) return null;
    const headset = devices.find((d) => looksLikeHeadsetOrBluetooth(d.label));
    if (headset) return headset.deviceId;
    const sysDefault = devices.find((d) => looksLikeDefaultDevice(d.label, d.deviceId));
    return sysDefault?.deviceId || null;
  } catch {
    return null;
  }
}

async function applyPlaybackDevice(track, deviceId) {
  if (!track || !deviceId) return;
  try {
    if (typeof track.setPlaybackDevice === "function") {
      await track.setPlaybackDevice(deviceId);
    }
  } catch {
    // Safari / Firefox often lack setSinkId.
  }
  try {
    await track.play?.();
  } catch {
    /* autoplay / already playing */
  }
}

async function applyMicrophoneDevice(track, deviceId) {
  if (!track || !deviceId) return;
  try {
    if (typeof track.setDevice === "function") {
      await track.setDevice(deviceId);
    }
  } catch {
    /* device unplugged mid-switch */
  }
}

async function syncCallAudioDevices(handle, AgoraRTC) {
  if (!handle) return;
  const micId = await pickPreferredMicrophoneId(AgoraRTC);
  const playId = await pickPreferredPlaybackId(AgoraRTC);
  await applyMicrophoneDevice(handle.localAudioTrack, micId);
  for (const track of handle.remoteAudioTracks || []) {
    await applyPlaybackDevice(track, playId);
  }
}

function attachDeviceListeners(handle, AgoraRTC) {
  const sync = () => {
    if (handle.deviceSyncTimer) clearTimeout(handle.deviceSyncTimer);
    handle.deviceSyncTimer = setTimeout(() => {
      handle.deviceSyncTimer = null;
      void syncCallAudioDevices(handle, AgoraRTC);
    }, DEVICE_SYNC_DEBOUNCE_MS);
  };

  if (typeof navigator !== "undefined" && navigator.mediaDevices?.addEventListener) {
    handle.deviceChangeHandler = sync;
    navigator.mediaDevices.addEventListener("devicechange", sync);
  }

  // Agora fires per-device plug/unplug (Windows/macOS/Linux Chrome).
  handle.agoraMicHandler = () => sync();
  handle.agoraPlayHandler = () => sync();
  try {
    AgoraRTC.onMicrophoneChanged = handle.agoraMicHandler;
    AgoraRTC.onPlaybackDeviceChanged = handle.agoraPlayHandler;
  } catch {
    /* older SDK */
  }
}

function detachDeviceListeners(handle, AgoraRTC) {
  if (!handle) return;
  if (handle.deviceSyncTimer) {
    clearTimeout(handle.deviceSyncTimer);
    handle.deviceSyncTimer = null;
  }
  if (handle.deviceChangeHandler) {
    try {
      navigator.mediaDevices?.removeEventListener?.("devicechange", handle.deviceChangeHandler);
    } catch {
      /* ignore */
    }
    handle.deviceChangeHandler = null;
  }
  try {
    if (AgoraRTC && AgoraRTC.onMicrophoneChanged === handle.agoraMicHandler) {
      AgoraRTC.onMicrophoneChanged = undefined;
    }
    if (AgoraRTC && AgoraRTC.onPlaybackDeviceChanged === handle.agoraPlayHandler) {
      AgoraRTC.onPlaybackDeviceChanged = undefined;
    }
  } catch {
    /* ignore */
  }
  handle.agoraMicHandler = null;
  handle.agoraPlayHandler = null;
}

function liveHandle(client) {
  return (
    client?.__naseebiLiveHandle || {
      client,
      localAudioTrack: client?.localAudioTrack || null,
      remoteAudioTracks: new Set(),
    }
  );
}

/**
 * Join a support/CSR/sales voice channel as agent (publish mic, play remote audio).
 * Follows OS default + headset/BT on Windows, Linux, and macOS, including mid-call switches.
 */
export async function joinSupportAgoraCall(session, options = {}) {
  if (joinInFlight) {
    await joinInFlight.catch(() => {});
  }

  const run = async () => {
    const { onRemoteUserJoined, onRemoteUserLeft } = options;
    const { appId, channelName, token, uid } =
      typeof session?.token === "string" ? parseSupportAgoraSession(session) : session;

    if (activeSupportClient) {
      await leaveSupportAgoraCall(
        activeSupportClient,
        activeSupportClient.localAudioTrack
      );
    }

    const perm = await ensureMicrophonePermission();
    if (!perm.granted) {
      const err = new Error("Microphone access is required to start a call.");
      err.code = perm.reason === "denied" ? "PERMISSION_DENIED" : "NO_MICROPHONE";
      throw err;
    }

    const AgoraRTC = (await import("agora-rtc-sdk-ng")).default;
    const joinOnce = async () => {
      const client = AgoraRTC.createClient({ mode: "rtc", codec: "vp8" });
      const handle = {
        client,
        localAudioTrack: null,
        remoteAudioTracks: new Set(),
        deviceChangeHandler: null,
        agoraMicHandler: null,
        agoraPlayHandler: null,
        deviceSyncTimer: null,
        AgoraRTC,
      };
      client.__naseebiLiveHandle = handle;

      client.on("user-published", async (user, mediaType) => {
        if (mediaType !== "audio") return;
        await client.subscribe(user, mediaType);
        if (user.audioTrack) {
          handle.remoteAudioTracks.add(user.audioTrack);
          const playId = await pickPreferredPlaybackId(AgoraRTC);
          await applyPlaybackDevice(user.audioTrack, playId);
        } else {
          user.audioTrack?.play();
        }
        onRemoteUserJoined?.(user);
      });

      client.on("user-unpublished", (user, mediaType) => {
        if (mediaType === "audio" && user.audioTrack) {
          handle.remoteAudioTracks.delete(user.audioTrack);
        }
      });

      client.on("user-joined", (user) => {
        onRemoteUserJoined?.(user);
      });

      client.on("user-left", (user) => {
        onRemoteUserLeft?.(user);
      });

      await client.join(appId, channelName, token, uid);

      const preferredMicId = await pickPreferredMicrophoneId(AgoraRTC);
      const localAudioTrack = await AgoraRTC.createMicrophoneAudioTrack({
        ...MIC_CONSTRAINTS,
        encoderConfig: "speech_standard",
        ...(preferredMicId ? { microphoneId: preferredMicId } : {}),
      });
      try {
        localAudioTrack.setVolume?.(100);
      } catch {
        /* ignore */
      }
      handle.localAudioTrack = localAudioTrack;
      await client.publish([localAudioTrack]);

      const preferredPlayId = await pickPreferredPlaybackId(AgoraRTC);
      for (const user of client.remoteUsers) {
        if (user.hasAudio) {
          await client.subscribe(user, "audio");
          if (user.audioTrack) {
            handle.remoteAudioTracks.add(user.audioTrack);
            await applyPlaybackDevice(user.audioTrack, preferredPlayId);
          }
          onRemoteUserJoined?.(user);
        }
      }

      attachDeviceListeners(handle, AgoraRTC);
      client.localAudioTrack = localAudioTrack;
      activeSupportClient = client;
      return { client, localAudioTrack };
    };

    try {
      return await joinOnce();
    } catch (err) {
      if (!isAgoraConflictError(err)) throw err;
      await leaveSupportAgoraCall(activeSupportClient, activeSupportClient?.localAudioTrack);
      activeSupportClient = null;
      return joinOnce();
    }
  };

  joinInFlight = run();
  try {
    return await joinInFlight;
  } finally {
    joinInFlight = null;
  }
}

export async function leaveSupportAgoraCall(client, localAudioTrack) {
  try {
    const handle = liveHandle(client);
    detachDeviceListeners(handle, handle.AgoraRTC);
    handle.remoteAudioTracks?.clear?.();
    localAudioTrack?.close();
    handle.localAudioTrack?.close?.();
    await client?.leave();
  } catch {
    // ignore cleanup errors
  } finally {
    if (client && activeSupportClient === client) {
      activeSupportClient = null;
    }
  }
}

export function formatSupportCallError(error) {
  const code = String(error?.code || "").trim();
  const message = String(error?.response?.data?.message || error?.message || "").trim();
  const combined = `${code} ${message}`.toUpperCase();

  if (combined.includes("ADAPTER_SEND_CONFLICT") || combined.includes("UID_CONFLICT")) {
    return "Could not connect to the call channel. Close any other call tabs and try again.";
  }
  if (combined.includes("CHANNEL_CONFLICT")) {
    return "You are already connected to this call in another window.";
  }
  if (combined.includes("PERMISSION_DENIED") || combined.includes("NOT_ALLOWED")) {
    return "Microphone access is required to start a call.";
  }
  if (combined.includes("NO_MICROPHONE") || combined.includes("NOT_FOUND")) {
    return "No microphone found. Plug in a headset or enable the laptop mic, then try again.";
  }

  return message || code || "Call failed";
}
